import type { Edge, Node } from '@xyflow/react';

import type { IEdgeWiringData } from '../edge-wiring-chip';

import { computeEdgeWiringData } from './auto-wire';
import { getNodeTypeInputFieldNames } from './effective-input-schema';
import type { OutputSchemaContext } from './effective-output-schema';
import { getEffectiveOutputFieldNamesByNodeKey } from './effective-output-schema';

export function annotateEdgeWithWiring(
  edge: Edge,
  edges: Edge[],
  nodes: Node[],
  nodeTypeInputSchemaMap: ReadonlyMap<string, Record<string, unknown> | null>,
  schemaCtx: OutputSchemaContext,
): Edge {
  const source = nodes.find((n) => n.id === edge.source);
  const target = nodes.find((n) => n.id === edge.target);
  if (!source || !target) return edge;

  const connectedSourcesToTarget = new Set(
    edges.filter((e) => e.target === edge.target).map((e) => e.source),
  );

  const targetData = target.data as {
    nodeType?: string;
    inputMapping?: Record<string, unknown>;
  };
  const wiring = computeEdgeWiringData({
    sourceKey: edge.source,
    sourceFields: getEffectiveOutputFieldNamesByNodeKey(
      (k) => nodes.find((n) => n.id === k) ?? null,
      edge.source,
      schemaCtx,
    ),
    targetInputFields: getNodeTypeInputFieldNames(
      String(targetData.nodeType ?? ''),
      nodeTypeInputSchemaMap,
    ),
    existingMapping: (targetData.inputMapping ?? {}) as Record<string, unknown>,
    connectedSourcesToTarget,
  });

  const prev = (edge.data ?? {}) as IEdgeWiringData & Record<string, unknown>;
  return {
    ...edge,
    data: {
      ...prev,
      autoWired: wiring.autoWired,
      wiringConflicts: wiring.wiringConflicts,
      wiringWarnings: wiring.wiringWarnings,
    },
  };
}

export function annotateEdgesWithWiring(
  edges: Edge[],
  nodes: Node[],
  nodeTypeInputSchemaMap: ReadonlyMap<string, Record<string, unknown> | null>,
  schemaCtx: OutputSchemaContext,
): Edge[] {
  return edges.map((e) =>
    annotateEdgeWithWiring(e, edges, nodes, nodeTypeInputSchemaMap, schemaCtx),
  );
}

export function edgesWiringEqual(a: Edge[], b: Edge[]): boolean {
  if (a.length !== b.length) return false;
  const byId = new Map(b.map((e) => [e.id, e]));
  for (const edge of a) {
    const other = byId.get(edge.id);
    if (!other) return false;
    const da = (edge.data ?? {}) as IEdgeWiringData;
    const db = (other.data ?? {}) as IEdgeWiringData;
    if (Boolean(da.autoWired) !== Boolean(db.autoWired)) return false;
    if (JSON.stringify(da.wiringConflicts ?? []) !== JSON.stringify(db.wiringConflicts ?? [])) {
      return false;
    }
    if (JSON.stringify(da.wiringWarnings ?? []) !== JSON.stringify(db.wiringWarnings ?? [])) {
      return false;
    }
  }
  return true;
}
